import React, { useState } from "react";
import { FaLaptop, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import { registerTrustedDevice } from "../api/trustedDevicesApi";
import { buildTrustedDevicePayload } from "../../../utils/trustedDevice";

interface TrustedDeviceRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId?: string;
  onRegistered?: () => void;
}

const TrustedDeviceRegistrationModal: React.FC<
  TrustedDeviceRegistrationModalProps
> = ({ isOpen, onClose, deviceId, onRegistered }) => {
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const payload = buildTrustedDevicePayload();
  const displayDeviceId = deviceId || payload.deviceId;

  const handleRegister = async () => {
    setSubmitting(true);
    try {
      await registerTrustedDevice({
        deviceId: displayDeviceId,
        userAgent: payload.userAgent,
        platform: payload.platform,
        timezone: payload.timezone,
        language: payload.language,
        screen: payload.screen,
        label: label.trim() || undefined,
      });
      toast.success(
        "Device registration submitted. HR must approve it before you can clock in.",
      );
      onRegistered?.();
      onClose();
    } catch (error: unknown) {
      console.error("Trusted device registration failed:", error);
      toast.error("Could not register this device. Please try again or contact HR.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        role="dialog"
        aria-labelledby="trusted-device-modal-title"
      >
        <div className="bg-purple-900 text-white px-6 py-4 flex items-center gap-3">
          <FaLaptop className="text-2xl shrink-0" aria-hidden />
          <h2 id="trusted-device-modal-title" className="text-lg font-semibold">
            Register office device
          </h2>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm text-gray-700">
          <p>
            This laptop is not on the trusted device list yet. Submit a
            registration request so HR can approve it for clock-in.
          </p>

          <div>
            <label
              htmlFor="trusted-device-label"
              className="block text-gray-600 mb-1 font-medium"
            >
              Device label (optional)
            </label>
            <input
              id="trusted-device-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Office laptop — Sales"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={submitting}
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Device ID (for HR reference)</p>
            <p className="font-mono text-xs break-all text-gray-800">{displayDeviceId}</p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRegister}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-purple-900 text-white hover:bg-purple-800 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <FaSpinner className="animate-spin" aria-hidden />
                Submitting…
              </>
            ) : (
              "Register this device"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrustedDeviceRegistrationModal;
